import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  HydratedOrganizationFile,
  ORGANIZATION_FILE_ENTRY_TYPE,
  splitFileName,
} from "@forgepoint/shared";
import { useFilesStore } from "./filesStore";

interface RenameFileDialogProps {
  /** Id of the entry to rename; null keeps the dialog closed. */
  fileId: string | null;
  onClose: () => void;
  onRenamed: (file: HydratedOrganizationFile) => void;
}

function editableBaseName(file: HydratedOrganizationFile): string {
  return file.type === ORGANIZATION_FILE_ENTRY_TYPE.FOLDER
    ? file.name
    : splitFileName(file.name).baseName;
}

/**
 * Dialog for renaming a vault entry. File extensions stay locked and are
 * re-appended on save, so a rename cannot change the file type; folder names
 * are edited in full.
 */
export function RenameFileDialog({
  fileId,
  onClose,
  onRenamed,
}: RenameFileDialogProps) {
  const updateFile = useFilesStore((state) => state.updateFile);
  const fileLoadingStates = useFilesStore((state) => state.fileLoadingStates);
  const file = useFilesStore((state) =>
    fileId ? state.files.get(fileId) : undefined,
  );
  const [baseName, setBaseName] = useState("");

  const isFolder = file?.type === ORGANIZATION_FILE_ENTRY_TYPE.FOLDER;
  const extension = file && !isFolder ? splitFileName(file.name).extension : "";
  const isRenaming = file
    ? (fileLoadingStates.get(file.id)?.isLoadingUpdate ?? false)
    : false;

  useEffect(() => {
    if (file) {
      setBaseName(editableBaseName(file));
    }
  }, [file]);

  const handleRename = async () => {
    if (!file || !baseName.trim()) {
      return;
    }

    const newName = baseName.trim() + extension;
    if (newName === file.name) {
      onClose();
      return;
    }

    const updatedFile = await updateFile({
      organizationFileId: file.id,
      name: newName,
    });
    if (updatedFile) {
      toast.success(isFolder ? "Folder renamed" : "File renamed");
      onClose();
      onRenamed(updatedFile);
    } else {
      toast.error(
        isFolder ? "Failed to rename folder" : "Failed to rename file",
      );
    }
  };

  return (
    <Dialog
      open={file !== undefined}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            handleRename();
          }}
        >
          <DialogHeader>
            <DialogTitle>
              {isFolder ? "Rename folder" : "Rename file"}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-2">
            <Label htmlFor="vault-rename-name">Name</Label>
            <div className="flex items-center gap-2">
              <Input
                id="vault-rename-name"
                value={baseName}
                onChange={(event) => setBaseName(event.target.value)}
                disabled={isRenaming}
                autoFocus
              />
              {extension && (
                <span className="text-muted-foreground shrink-0">
                  {extension}
                </span>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isRenaming}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!baseName.trim() || isRenaming}>
              {isRenaming ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Rename"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
